import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Divider, Grid, IconButton, InputAdornment, Toolbar, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Redirect, useHistory } from 'react-router';

import { DataAPIContext } from '../api';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageMeta from '../components/PageMeta';
import StyledFilledTextField from '../components/StyledFilledTextField';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import CampaignListGrid from './CampaignListGrid';
import NewCampaignDialog from './NewCampaignDialog';
import {
    deleteCampaignApi,
    duplicateCampaignApi,
    fetchCampaignList,
    userCanAccessCampaignsUi,
} from './campaignStorage';
import { ConfirmationDialogText } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#FCFCFE',
        paddingBottom: '1rem !important',
    },
    toolbarRoot: {
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    // CampaignListGrid's DataGrid takes its height from this className via
    // its ``classes?.recordList`` prop — without it the grid collapses to 0px
    // and the rows render invisibly even though the footer shows the count.
    recordListWrapper: {
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 15rem) !important`,
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        height: `calc(${theme.cp.contentHeight} - 11.5rem) !important`,
    },
}));

const CampaignList = () => {
    const classes = useStyles();
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm } = React.useContext(ContextStore);
    const isSmallScreen = !useIsWidthUp('sm');
    const btSize = isSmallScreen ? 'medium' : 'small';

    const [campaignRows, setCampaignRows] = React.useState([]);
    const [query, setQuery] = React.useState('');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [newCampaignDialogOpen, setNewCampaignDialogOpen] = React.useState(false);
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [active, setActive] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const [syncActive, setSyncActive] = React.useState(false);

    const searchTimeoutRef = React.useRef(null);

    const refreshRows = React.useCallback(() => {
        return fetchCampaignList(dataAPI)
            .then((rows) => setCampaignRows(rows))
            .catch((err) => {
                console.error(err);
                enqueueSnackbar('Could not load campaigns', { variant: 'error' });
            });
    }, [dataAPI, enqueueSnackbar]);

    React.useEffect(() => {
        refreshRows();
    }, [refreshRows]);

    const handlers = React.useMemo(
        () => ({
            // Edit navigation is handled directly by the Action column's edit
            // button, which is an anchor that opens the campaign in a new tab
            // (CP-8032). Campaign Analytics works the same way (CP-8145): its
            // icon is an anchor deep-linking to ?step=analytics in a new tab
            // (CampaignForm reads the step from the query string on mount), so
            // neither action needs a handler here.
            onDuplicate: (row) => {
                setActive(row);
                setConfirmationDialogAction('duplicate_campaign');
            },
            onDelete: (row) => {
                setIdsToDelete([row.id]);
                setActive(row);
                setConfirmationDialogAction('delete_campaign');
            },
        }),
        []
    );

    const handleCreateCampaign = () => setNewCampaignDialogOpen(true);

    const handleNewCampaignSelect = async ({ mode, sourceId }) => {
        setNewCampaignDialogOpen(false);
        if (mode === 'new') {
            history.push('/notifications/campaigns/create');
            return;
        }
        if (mode === 'existing' && sourceId != null) {
            setSyncActive(true);
            try {
                const clone = await duplicateCampaignApi(dataAPI, sourceId);
                if (clone?.id) {
                    history.push(`/notifications/campaigns/${clone.id}`);
                }
            } catch (err) {
                console.error(err);
                enqueueSnackbar('Could not clone campaign', { variant: 'error' });
            } finally {
                setSyncActive(false);
            }
        }
    };

    const confirmationDialogCancel = () => setConfirmationDialogAction(null);

    const doDuplicate = () => {
        if (!active) return;
        setSyncActive(true);
        duplicateCampaignApi(dataAPI, active.id)
            .then(() => {
                refreshRows();
                enqueueSnackbar('Campaign duplicated', { variant: 'success' });
            })
            .catch((err) => {
                console.error(err);
                enqueueSnackbar('Error duplicating campaign', { variant: 'error' });
            })
            .finally(() => setSyncActive(false));
    };

    const doDelete = () => {
        if (!idsToDelete || !idsToDelete.length) return;
        const id = idsToDelete[0];
        setSyncActive(true);
        deleteCampaignApi(dataAPI, id)
            .then(() => {
                refreshRows();
                enqueueSnackbar('Campaign deleted', { variant: 'default' });
            })
            .catch((err) => {
                console.error(err);
                enqueueSnackbar('Error deleting campaign', { variant: 'error' });
            })
            .finally(() => setSyncActive(false));
    };

    const confirmationDialogConfirm = () => {
        if (confirmationDialogAction === 'duplicate_campaign') doDuplicate();
        else if (confirmationDialogAction === 'delete_campaign') doDelete();
        confirmationDialogCancel();
    };

    const onQueryChange = (e) => {
        const v = e.target.value;
        setSearchTerm(v);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => setQuery(v), 500);
    };

    React.useEffect(() => () => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    }, []);

    if (!userCanAccessCampaignsUi()) {
        return <Redirect to="/notifications" />;
    }

    return (
        <div className={classes.root}>
            <FixedLinearProgress visibility={syncActive} />
            <PageMeta title="Campaigns" />
            {confirmationDialogAction && (
                <ConfirmationDialog
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {confirmationDialogAction === 'duplicate_campaign' && (
                        <Typography>
                            {ConfirmationDialogText[confirmationDialogAction].body}
                            <span style={{ fontWeight: 700 }}>{active?.name || 'Campaign'}</span>?
                        </Typography>
                    )}
                    {confirmationDialogAction === 'delete_campaign' && (
                        <Typography>
                            {ConfirmationDialogText[confirmationDialogAction].body}
                            <span style={{ fontWeight: 700 }}>{active?.name || 'Campaign'}</span> This action is irreversible. Are you sure?
                        </Typography>
                    )}
                </ConfirmationDialog>
            )}

            <NewCampaignDialog
                open={newCampaignDialogOpen}
                onClose={() => setNewCampaignDialogOpen(false)}
                onSelect={handleNewCampaignSelect}
                existingCampaigns={campaignRows}
            />

            <Grid item xs={12} pt={2} pl={2} pr={2} container justifyContent="space-between" alignItems="center">
                <Typography fontSize={20} fontWeight={500}>Campaigns</Typography>
                {hasPerm('campaign', 'write') && !isSmallScreen && (
                    <Box>
                        <Button color="primary" variant="contained" size={btSize} onClick={handleCreateCampaign}>
                            <AddIcon />
                            &nbsp;New campaign
                        </Button>
                    </Box>
                )}
            </Grid>
            <Grid item xs={12} style={{ margin: '1rem', border: 'solid 1px #D8D8D8', borderRadius: '12px' }}>
                <Grid item xs={12} p={isSmallScreen && 2} pb={0}>
                    <Toolbar disableGutters className={classes.toolbarRoot}>
                        <Grid container direction="row" spacing={2} alignItems="center" wrap="nowrap">
                            <Grid item xs sm={6}>
                                <StyledFilledTextField
                                    label="Search campaigns"
                                    size="small"
                                    onChange={onQueryChange}
                                    value={searchTerm}
                                    fullWidth
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton aria-label="search campaigns">
                                                    <SearchIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            {/* On a phone the [+] sits at the end of the search row, as on Activities (CP-5563). */}
                            {hasPerm('campaign', 'write') && isSmallScreen && (
                                <Grid item xs="auto">
                                    <Button color="primary" variant="contained" onClick={handleCreateCampaign}>
                                        <AddIcon />
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Toolbar>
                </Grid>
                <Divider />
                <Grid item xs={12} className={classes.recordListWrapper}>
                    <CampaignListGrid
                        rows={campaignRows}
                        classes={classes}
                        campaignQuery={query}
                        handlers={handlers}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default CampaignList;
